/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { MutableRefObject } from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import type { IconProps } from '@coursera/cds-common';
import { Link, Typography2 } from '@coursera/cds-core';
import { BookmarkIcon, BriefcaseIcon, CodeIcon, LargeGridIcon, TrendingIcon } from '@coursera/cds-icons';
import type { Button } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type { ProductVariantCardsFragment } from 'bundles/premium-hub/__generated__/contentfulTypes';
import { getFormattedCategoryLinks } from 'bundles/premium-hub/components/degrees/DegreesCategoryCards';
import Container from 'bundles/premium-hub/components/shared/Container';
import { DEPRECATED_CATEGORY_IDS } from 'bundles/premium-hub/constants';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { PPCDegreeCategory } from 'bundles/premium-hub/types';

import _t from 'i18n!nls/premium-hub';

type Props = {
  categoryLinks?: Array<Maybe<ProductVariantCardsFragment>>;
  categories?: Array<PPCDegreeCategory>;
};

const CATEGORY_ICONS: Record<string, React.ComponentType<IconProps>> = {
  BUSINESS: BriefcaseIcon,
  DATA_SCIENCE: TrendingIcon,
  COMPUTER_SCIENCE: CodeIcon,
  DATA_ANALYTICS: LargeGridIcon,
};

const styles = {
  wrapper: css`
    padding: var(--cds-spacing-100) 0;
  `,
  carousel: css`
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: 1fr;
    gap: var(--cds-spacing-100);
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  chip: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    white-space: nowrap;
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-max);
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    text-decoration: none;
  `,
  icon: css`
    display: flex;
    flex-shrink: 0;
  `,
};

const AreasOfStudyChips = ({ categoryLinks = [], categories = [] }: Props) => {
  const trackV3 = useTracker();
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const sectionRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: {
      sectionName: 'browse_domains',
      index: sectionIndex,
    },
  });

  if (!categoryLinks.length) {
    return null;
  }

  const categoryChips = getFormattedCategoryLinks(categoryLinks, categories).filter(
    (category) => !DEPRECATED_CATEGORY_IDS.includes(category.id)
  );

  if (!categoryChips.length) {
    return null;
  }

  return (
    <Container>
      <div css={styles.wrapper} ref={sectionRef}>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
          {_t('Areas of study')}
        </Typography2>
        <OverflowCarousel css={styles.carousel}>
          {categoryChips.map(({ title, href, id }) => {
            const CategoryIcon = CATEGORY_ICONS[id] ?? BookmarkIcon;
            return (
              <Link
                key={id}
                href={href}
                css={styles.chip}
                onClick={() =>
                  trackV3('click_button', {
                    button: {
                      name: 'degrees_category_hub',
                      linkType: 'internal',
                      linkName: title,
                      linkURL: href,
                    } as Button,
                    pageSection: {
                      sectionName: 'browse_domains',
                      index: sectionIndex,
                    },
                  })
                }
              >
                <CategoryIcon size="small" css={styles.icon} />
                <Typography2 variant="bodySecondary" component="span">
                  {title}
                </Typography2>
              </Link>
            );
          })}
        </OverflowCarousel>
      </div>
    </Container>
  );
};

export default React.memo(AreasOfStudyChips);
